import React, { useMemo } from 'react';
import { Bar } from 'react-chartjs-2';
import { Chart as ChartJS, CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend } from 'chart.js';

ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend);

const GameAnalyzerChart = ({chartType, data, options, chartLabel, horizontalLine=null}) => {
    const horizontalLinePlugin = {
        id: 'horizontalLine',
        afterDraw: (chart) => {
          const value = chart?.options?.plugins?.horizontalLine?.value;
          if (value == null) return;
      
          const { ctx, chartArea, scales } = chart;
          if (!chartArea || !scales?.y) return;
      
          const { left, right } = chartArea;
          const yPos = scales.y.getPixelForValue(value);
      
          ctx.save();
          ctx.beginPath();
          ctx.lineWidth = 2;
          ctx.strokeStyle = 'black';
          ctx.moveTo(left, yPos);
          ctx.lineTo(right, yPos);
          ctx.stroke();
          ctx.restore();
        }
    };

    const getOrCreateLegendList = (id) => {
        const legendContainer = document.getElementById(id);
        if(!legendContainer) return null;

        let listContainer = legendContainer.querySelector('ul');
  
        if (!listContainer) {
            listContainer = document.createElement('ul');
            listContainer.style.display = 'flex';
            listContainer.style.flexDirection = 'row';
            listContainer.style.margin = 0;
            listContainer.style.padding = 0;
            listContainer.style.justifyContent = 'center';
            listContainer.style.flexWrap = 'wrap';
        
            legendContainer.appendChild(listContainer);
        };  
        return listContainer;
    };

    const htmlLegendPlugin = useMemo(() => {
        const legend = document.getElementById('legend-container')
        
        if (chartType === 'spread') {
            if(legend) legend.style.display = 'none';
            return null; // hide legend for 'spread'
        };
        if(legend) legend.style.display = 'block';

        return {
            id: 'htmlLegend',
            afterUpdate(chart) {
                const ul = getOrCreateLegendList('legend-container');
    
                while (ul.firstChild) ul.firstChild.remove();
    
                const items = chart.options.plugins.legend.labels.generateLabels(chart);
                items.forEach((item) => {
                    const li = document.createElement('li');
                    li.style.alignItems = 'center';
                    li.style.cursor = 'pointer';
                    li.style.display = 'flex';
                    li.style.flexDirection = 'row';
                    li.style.marginLeft = '10px';

                    li.onclick = () => {
                        const {type} = chart.config;
                        if (type === 'pie' || type === 'doughnut') {
                          // Pie and doughnut charts only have a single dataset and visibility is per item
                          chart.toggleDataVisibility(item.index);
                        } else {
                          chart.setDatasetVisibility(item.datasetIndex, !chart.isDatasetVisible(item.datasetIndex));
                        }
                        chart.update();
                    };
    
                    const boxSpan = document.createElement('span');
                    boxSpan.style.background = item.fillStyle;
                    boxSpan.style.borderColor = item.strokeStyle;
                    boxSpan.style.borderWidth = item.lineWidth + 'px';
                    boxSpan.style.display = 'inline-block';
                    boxSpan.style.flexShrink = 0;
                    boxSpan.style.height = '20px';
                    boxSpan.style.marginRight = '10px';
                    boxSpan.style.width = '20px';
            
                    const textContainer = document.createElement('p');
                    textContainer.style.margin = 0;
                    textContainer.style.padding = 0;
                    textContainer.style.textDecoration = item.hidden ? 'line-through' : '';
                    const text = document.createTextNode(item.text);
                    textContainer.appendChild(text);
    
                    li.appendChild(boxSpan);
                    li.appendChild(textContainer);
                    ul.appendChild(li);
                });
    

            },
        };
    }, [chartType]);


    const memoOptions = useMemo(() => {
        // start from incoming options
        const base = options ?? {};
        const basePlugins = base.plugins ?? {};
        return {
          ...base,
          responsive: true,
          maintainAspectRatio: false,
          plugins: {
            ...basePlugins,
            horizontalLine: { value: horizontalLine } // <-- reactive value
          }
        };
    }, [options, horizontalLine]);

    const plugins = useMemo(() => {
        return [horizontalLinePlugin, htmlLegendPlugin].filter(Boolean)
    }, [horizontalLinePlugin, htmlLegendPlugin]);

    return (
        <>
            <div className="text-center mb-2">
                <span id='chart-label' className="font-medium text-gray-700 dark:text-gray-300">{chartLabel}</span>
            </div>
            <div id='legend-container' className="text-center mb-3 flex justify-center"></div>

            <div className="relative w-full sm:h-50 lg:h-96">
                <Bar data={data} options={memoOptions} plugins={plugins}/>
            </div>
        </>
    );
};

export default GameAnalyzerChart;
